import CustomViewOptionCustomSection from "csi/middleware/customView/options/CustomViewOptionCustomSection";
import { isDefined } from "csi/utility/coreUtility";
import { Box, Checkbox, FlexColumn, FlexRow, OverflowText, SingleSelect, TextField } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import ImageExportFormat from "src/Export/ExportTable/ImageExportFormat";
import { ImageSize } from "src/c8-platform/type/image";
import { useCustomViewDialogStore } from "../CustomViewDialogStore";
import CheckboxOption from "./CheckboxOption";
import css from "./CustomViewOptions.module.css";
import FilterOptions from "./FilterOptions";
import TableCustomViewOptionsStore from "./TableCustomViewOptionsStore";

const TableViewOptions: FunctionComponent = observer(function TableViewOptions() {
	const { imageFormatStore, optionsStore } = useCustomViewDialogStore();

	const {
		hasImageHeightValidationError,
		imageHeight,
		imageHeightValidationMsg,
		selectedExpandLevel,
		selectedImageSizes,
		selectedQuerySize,
		selectedTableFreezeColumns,
		selectedTableHeight,
		setExpandLevel,
		setImageHeight,
		setImageSize,
		setQuerySize,
		setShowFileSizes,
		setTableAutomaticHeight,
		setTableFreezeColumns,
		setTableHeight,
		setTableHideRoot,
		tableDropDownOptions,
		tableOptions,
		toggleCollapsedViewMainOptions,
		viewMainOptionsCollapse,
	} = optionsStore as TableCustomViewOptionsStore;

	const {
		showFileSizes,
		tableAutomaticHeight,
		tableHideRoot,
	} = tableOptions;

	const { expandLevels, freezeColumns, resultCounts, tableHeights } = tableDropDownOptions;
	let { imageSizes } = tableDropDownOptions;
	// remove image icon size icon for exportTable view
	if (imageFormatStore) {
		imageSizes = imageSizes.filter((item) => item.id != "ImageSize:Icon");
	}

	return (
		<CustomViewOptionCustomSection
			collapsed={viewMainOptionsCollapse}
			dataCsiAutomation="TableViewOptions"
			onCollapse={toggleCollapsedViewMainOptions}
			titleRes="CustomViewTableOptions"
		>
			<FlexRow className={css.tableSectionTitleContainer}>
				<OverflowText
					className={css.tableSectionTitle}
					res="CustomViewDisplayOptionsAppearance"
				/>
			</FlexRow>
			<Box
				className={css.container}
				data-csi-automation="CustomViewDisplayOptionsAppearance"
			>
				<FlexRow>
					{isDefined(selectedTableHeight) && (
						<FlexColumn
							className={css.tableOptionLeft}
							data-csi-automation="CustomViewTableHeight"
						>
							<SingleSelect
								disableClearable
								fullWidth
								labelRes="CustomViewTableHeight"
								onChange={setTableHeight}
								options={tableHeights}
								value={selectedTableHeight}
							/>
						</FlexColumn>
					)}
					{isDefined(tableAutomaticHeight) && (
						<FlexColumn
							className={css.tableOptionRightCheck}
							data-csi-automation="CustomViewTableAutomaticHeight"
						>
							<Checkbox
								checked={tableAutomaticHeight}
								labelProps={{
									res: "ViewPreferences_tableAutomaticHeight",
								}}
								onChange={setTableAutomaticHeight}
							/>
						</FlexColumn>
					)}
				</FlexRow>
			</Box>
			{isDefined(selectedQuerySize) && (
				<Box className={css.container}>
					<FlexColumn
						className={css.tableOptionLeft}
						data-csi-automation="CustomViewResultCount"
					>
						<SingleSelect
							disableClearable
							fullWidth
							labelRes="CustomViewResultCount"
							onChange={setQuerySize}
							options={resultCounts}
							value={selectedQuerySize}
						/>
					</FlexColumn>
				</Box>
			)}
			{isDefined(selectedExpandLevel) && (
				<Box className={css.container}>
					<FlexColumn
						className={css.tableOptionLeft}
						data-csi-automation="CustomViewTableExpandLevel"
					>
						<SingleSelect
							disableClearable
							fullWidth
							labelRes="CustomViewTableExpandLevel"
							onChange={setExpandLevel}
							options={expandLevels}
							value={selectedExpandLevel}
						/>
					</FlexColumn>
				</Box>
			)}
			{isDefined(selectedTableFreezeColumns) && (
				<Box className={css.container}>
					<FlexColumn
						className={css.tableOptionLeft}
						data-csi-automation="CustomViewTableFreeze"
					>
						<SingleSelect
							disableClearable
							fullWidth
							helperTextRes="CustomViewTableBasedAvailableSpace"
							labelRes="CustomViewTableFreeze"
							onChange={setTableFreezeColumns}
							options={freezeColumns}
							value={selectedTableFreezeColumns}
						/>
					</FlexColumn>
				</Box>
			)}
			{isDefined(selectedImageSizes) && (
				<Box className={css.container}>
					<FlexRow>
						<FlexColumn
							className={css.tableOptionLeft}
							data-csi-automation="CustomViewImageSize"
						>
							<SingleSelect
								disableClearable
								fullWidth
								labelRes="CustomViewImageSize"
								onChange={setImageSize}
								options={imageSizes}
								value={selectedImageSizes}
							/>
						</FlexColumn>
						<FlexColumn
							className={css.tableOptionRightSelect}
							data-csi-automation="CustomViewTableImageHeight"
						>
							<Box hidden={!(selectedImageSizes.id === ImageSize.IMAGE_SIZE_CUSTOM)}>
								<TextField
									error={!!hasImageHeightValidationError}
									fullWidth
									helperText={imageHeightValidationMsg}
									onChange={setImageHeight}
									placeholder=""
									res="CustomViewTableImageHeight"
									value={imageHeight}
								/>
							</Box>
						</FlexColumn>
					</FlexRow>
				</Box>
			)}
			<FlexRow className={css.tableSectionTitleContainer}>
				<OverflowText
					className={css.tableSectionTitle}
					res="RuleFilters"
				/>
			</FlexRow>
			<FilterOptions />
			<FlexRow className={css.tableSectionTitleContainer}>
				<OverflowText
					className={css.tableSectionTitle}
					res="CustomViewAdvancedOptions"
				/>
			</FlexRow>
			<CheckboxOption
				dataCsiAutomation="CustomViewShowFileSizes"
				label="CustomViewShowFileSizes"
				onChange={setShowFileSizes}
				optionValue={showFileSizes}
				paddingLeft={0}
			/>
			{imageFormatStore && (
				<Box className={css.container}>
					<FlexColumn
						className={css.tableOptionLeft}
						data-csi-automation="CustomViewImageExportFormat"
					>
						<ImageExportFormat />
					</FlexColumn>
				</Box>
			)}
			<CheckboxOption
				dataCsiAutomation="CustomViewTableHideRootRow"
				label="CustomViewTableHideRootRow"
				onChange={setTableHideRoot}
				optionValue={tableHideRoot}
				paddingLeft={0}
			/>
		</CustomViewOptionCustomSection>
	);
});

export default TableViewOptions;